import * as React from 'react'
import {
  AppBar,
  Avatar,
  Box,
  Divider,
  Drawer,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Toolbar,
  Typography
} from '@mui/material'
import { useQuery, gql } from '@apollo/client'

const OPEN_TICKET_COUNT = gql`
  query OpenTicketCount {
    allSupportTickets(limit: 200) {
      id status
    }
  }
`

const VENDOR_TICKET_COUNT = gql`
  query VendorTicketCount {
    restaurantSupportTickets(limit: 200) {
      id status
    }
  }
`
import MenuIcon from '@mui/icons-material/Menu'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import LogoutIcon from '@mui/icons-material/Logout'
import DevicesRoundedIcon from '@mui/icons-material/DevicesRounded'
import routes from '../../routes'
import RushmyFoodLogo from '../../assets/img/rushmyfood-logo.png'
import { useTranslation, withTranslation } from 'react-i18next'
import { useLocation, useHistory } from 'react-router-dom'
import { useApolloClient } from '@apollo/client'
import SignOutAllDevicesDialog from '../Security/SignOutAllDevicesDialog'

const drawerWidth = 240
const BRAND = '#ED1D26'

// Section labels shown BEFORE the route with this path. Keyed by path rather
// than by position so inserting a route elsewhere cannot move the headings.
const SECTION_BEFORE = {
  '/vendors': 'GENERAL',
  '/admins': 'MANAGEMENT'
}

// Pages kept together under their own heading, in this order (the order of
// the work, not A–Z), placed just before the section named in `before`.
const PINNED_SECTIONS = [
  { section: 'PARTNER ONBOARDING', paths: ['/partner-signups', '/current-merchants', '/agents', '/signup-standards'], before: 'MANAGEMENT' }
]

function AdminSidebar(props) {
  const { t } = useTranslation()
  const location = useLocation()
  const history = useHistory()
  const client = useApolloClient()
  const { window } = props
  const [mobileOpen, setMobileOpen] = React.useState(false)
  const [anchorEl, setAnchorEl] = React.useState(null)
  const [signOutAll, setSignOutAll] = React.useState(false)
  const [userInfo, setUserInfo] = React.useState({ name: 'User', email: '' })

  React.useEffect(() => {
    const load = () => {
      const data = localStorage.getItem('user-enatega')
      if (data) {
        const p = JSON.parse(data)
        setUserInfo({ name: p.name || (p.email ? p.email.split('@')[0] : 'User'), email: p.email || '', userType: p.userType || '' })
      }
    }
    load()
    window !== undefined && window().addEventListener && window().addEventListener('userDataUpdated', load)
    return () => { window !== undefined && window().addEventListener && window().removeEventListener && window().removeEventListener('userDataUpdated', load) }
  }, [])

  const handleLogout = () => {
    setAnchorEl(null)
    localStorage.removeItem('user-enatega')
    localStorage.removeItem('restaurant_id')
    client.clearStore()
    history.push('/auth/login')
  }

  const handleProfile = () => {
    setAnchorEl(null)
    const data = localStorage.getItem('user-enatega')
    if (data) {
      const p = JSON.parse(data)
      history.push(p.userType === 'ADMIN' ? '/super_admin/profile' : '/admin/profile')
    }
  }

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen)
  const container = window !== undefined ? () => window().document.body : undefined

  // Admin sidebar routes, listed A–Z inside each section so a page is where
  // someone would look for it. Anything above the first heading (Home) keeps
  // its place, and a pinned section keeps its own order. Each item carries the
  // heading to draw before it, so the heading stays at the top of its section
  // whatever sorts first.
  const adminRoutes = React.useMemo(() => {
    const sidebar = routes.reduce((acc, prop, key) => {
      if (prop.appearInSidebar && prop.admin) acc.push({ ...prop, originalKey: key })
      return acc
    }, [])
    const pinned = new Set(PINNED_SECTIONS.flatMap(p => p.paths))

    const groups = []
    let group = { section: null, items: [] }
    for (const prop of sidebar) {
      if (pinned.has(prop.path)) continue
      const section = SECTION_BEFORE[prop.path]
      if (section) {
        groups.push(group)
        group = { section, items: [] }
      }
      group.items.push(prop)
    }
    groups.push(group)

    for (const pin of PINNED_SECTIONS) {
      const items = pin.paths.map(path => sidebar.find(r => r.path === path)).filter(Boolean)
      if (!items.length) continue
      const at = groups.findIndex(g => g.section === pin.before)
      groups.splice(at < 0 ? groups.length : at, 0, { section: pin.section, items, keepOrder: true })
    }

    return groups.flatMap(({ section, items, keepOrder }) => {
      const ordered = section && !keepOrder
        ? items.slice().sort((a, b) =>
          String(a.name).localeCompare(String(b.name), undefined, { sensitivity: 'base' }))
        : items
      return ordered.map((item, i) => ({ ...item, section: i === 0 ? section : null }))
    })
  }, [])

  const isVendor = userInfo.userType === 'VENDOR'

  // Admin badge — all open tickets across the platform
  const { data: ticketData } = useQuery(OPEN_TICKET_COUNT, {
    fetchPolicy: 'cache-and-network',
    pollInterval: 60000,
    skip: isVendor,
  })
  const openTicketCount = (ticketData?.allSupportTickets || []).filter(
    t => t.status === 'OPEN' || t.status === 'AWAITING_ADMIN' || t.status === 'AWAITING_RESTAURANT'
  ).length

  // Vendor badge — only their restaurant's action-needed + open tickets
  const { data: vendorTicketData } = useQuery(VENDOR_TICKET_COUNT, {
    fetchPolicy: 'cache-and-network',
    pollInterval: 60000,
    skip: !isVendor,
  })
  const vendorTicketCount = (vendorTicketData?.restaurantSupportTickets || []).filter(
    t => t.status === 'AWAITING_RESTAURANT' || t.status === 'OPEN'
  ).length

  const badgeCount = isVendor ? vendorTicketCount : openTicketCount

  const isActive = prop => location.pathname === `${prop.layout}${prop.path}` ||
    (prop.activePaths || []).some(p => location.pathname.startsWith(`${prop.layout}${p}/`))

  const IconEl = ({ icon: Icon, active }) => {
    if (!Icon) return null
    return (
      <Box className="rmf-nav-icon" sx={{
        width: 20, height: 20, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
        '& svg': {
          width: 20, height: 20,
          transition: 'filter 150ms ease',
          filter: active
            ? 'brightness(0) invert(1)'
            : 'brightness(0) invert(1) opacity(0.55)'
        }
      }}>
        <Icon />
      </Box>
    )
  }

  const DrawerContent = (
    <Box sx={{
      display: 'flex', flexDirection: 'column', height: '100%',
      bgcolor: '#0f172a', overflowY: 'auto', overflowX: 'hidden',
      '&::-webkit-scrollbar': { width: 0 }
    }}>
      {/* Logo area */}
      <Box sx={{ px: 2.5, pt: 2.5, pb: 2, borderBottom: '1px solid rgba(255,255,255,0.07)', flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 40, height: 40, borderRadius: '10px',
            bgcolor: '#fff', display: 'flex',
            alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            boxShadow: '0 1px 4px rgba(0,0,0,0.18)'
          }}>
            <img src={RushmyFoodLogo} alt="RushMyFood" style={{ width: 28, height: 28, objectFit: 'contain' }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: '#fff', lineHeight: 1.2 }}>
              RushMyFood
            </Typography>
            <Typography sx={{ fontSize: '0.62rem', color: 'rgba(255,255,255,0.4)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
              Admin Panel
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Nav links */}
      <Box sx={{ px: 1.5, py: 1.5, flex: 1 }}>
        {adminRoutes.map((prop, idx) => {
          const section = prop.section
          const active = isActive(prop)
          return (
            <React.Fragment key={prop.originalKey}>
              {section && (
                <Typography sx={{
                  fontSize: '0.6875rem', fontWeight: 600,
                  color: 'rgba(255,255,255,0.5)',
                  letterSpacing: '0.08em', textTransform: 'uppercase',
                  px: 1.5, mt: idx === 0 ? 0.5 : 3, mb: 1
                }}>
                  {section}
                </Typography>
              )}
              <Box
                component="a"
                href={'#' + prop.layout + prop.path}
                aria-current={active ? 'page' : undefined}
                sx={{
                  position: 'relative',
                  display: 'flex', alignItems: 'center', gap: 1.5,
                  px: 1.5, py: 0.9, mb: 0.25,
                  borderRadius: '10px', textDecoration: 'none', cursor: 'pointer',
                  bgcolor: active ? 'rgba(255,255,255,0.08)' : 'transparent',
                  transition: 'background-color 150ms ease',
                  '&::before': {
                    content: '""', position: 'absolute', left: 0, top: 8, bottom: 8, width: 3,
                    borderRadius: '0 3px 3px 0', bgcolor: BRAND,
                    transform: active ? 'scaleY(1)' : 'scaleY(0)', transition: 'transform 180ms ease'
                  },
                  '&:hover': {
                    bgcolor: active ? 'rgba(255,255,255,0.10)' : 'rgba(255,255,255,0.05)',
                    '& .rmf-nav-label': { color: '#fff' },
                    '& .rmf-nav-icon svg': { filter: 'brightness(0) invert(1) opacity(0.85)' }
                  },
                  '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 1 },
                  '@media (prefers-reduced-motion: reduce)': { '&::before': { transition: 'none' } }
                }}
              >
                <IconEl icon={prop.icon} active={active} />
                <Typography className="rmf-nav-label" sx={{
                  fontSize: '0.83rem',
                  fontWeight: active ? 600 : 400,
                  color: active ? '#fff' : 'rgba(255,255,255,0.7)',
                  lineHeight: 1,
                  flex: 1,
                  transition: 'color 150ms ease',
                }}>
                  {prop.name}
                </Typography>
                {prop.path === '/support-tickets' && badgeCount > 0 && (
                  <Box sx={{
                    minWidth: 18, height: 18, px: 0.6,
                    borderRadius: '10px', bgcolor: '#ef4444',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    <Typography sx={{ fontSize: '0.65rem', fontWeight: 800, color: '#fff', lineHeight: 1 }}>
                      {badgeCount > 99 ? '99+' : badgeCount}
                    </Typography>
                  </Box>
                )}
              </Box>
            </React.Fragment>
          )
        })}
      </Box>

      {/* User profile at bottom */}
      <Box sx={{ px: 1.5, pb: 2, flexShrink: 0, borderTop: '1px solid rgba(255,255,255,0.07)', pt: 1.5 }}>
        <Box
          onClick={e => setAnchorEl(e.currentTarget)}
          sx={{
            display: 'flex', alignItems: 'center', gap: 1.5,
            px: 1.5, py: 1, borderRadius: '10px', cursor: 'pointer',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' }
          }}>
          <Avatar sx={{ width: 32, height: 32, bgcolor: '#4ECDC4', fontSize: '14px', fontWeight: 'bold', flexShrink: 0 }}>
            {userInfo.name.charAt(0).toUpperCase()}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: '0.83rem', fontWeight: 600, color: '#fff', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {userInfo.name}
            </Typography>
            <Typography sx={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.4)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {userInfo.email}
            </Typography>
          </Box>
          <KeyboardArrowDownIcon sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 18, flexShrink: 0 }} />
        </Box>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          PaperProps={{ sx: { borderRadius: '8px', boxShadow: '0 4px 20px rgba(0,0,0,0.15)', minWidth: 160, mb: 1 } }}>
          <MenuItem onClick={handleProfile} sx={{ py: 1.2, fontSize: '0.85rem' }}>
            <ListItemIcon><PersonOutlineIcon sx={{ color: '#666' }} /></ListItemIcon>
            Profile
          </MenuItem>
          <MenuItem onClick={() => { setAnchorEl(null); setSignOutAll(true) }} sx={{ py: 1.2, fontSize: '0.85rem' }}>
            <ListItemIcon><DevicesRoundedIcon sx={{ color: '#666' }} /></ListItemIcon>
            Sign out of all devices
          </MenuItem>
          <Divider sx={{ my: 0.5 }} />
          <MenuItem onClick={handleLogout} sx={{ py: 1.2, fontSize: '0.85rem', color: '#ef4444' }}>
            <ListItemIcon><LogoutIcon sx={{ color: '#ef4444' }} /></ListItemIcon>
            Log Out
          </MenuItem>
        </Menu>
      </Box>
    </Box>
  )

  return (
    <>
      <AppBar position="fixed" sx={{
        width: { sm: `calc(100% - ${drawerWidth}px)` },
        ml: { sm: `${drawerWidth}px` },
        display: { sm: 'none' },
        bgcolor: '#0f172a'
      }}>
        <Toolbar>
          <IconButton color="inherit" aria-label="open drawer" edge="start"
            onClick={handleDrawerToggle} sx={{ mr: 2, display: { sm: 'none' } }}>
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <SignOutAllDevicesDialog open={signOutAll} onClose={() => setSignOutAll(false)} onSignedOut={handleLogout} />

      <Box component="nav" sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}>
        <Drawer container={container} variant="temporary" open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: '75%', bgcolor: '#0f172a', border: 'none' }
          }}>
          {DrawerContent}
        </Drawer>
        <Drawer variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              bgcolor: '#0f172a',
              border: 'none',
              borderRight: '1px solid rgba(255,255,255,0.06)'
            }
          }}
          open>
          {DrawerContent}
        </Drawer>
      </Box>
    </>
  )
}

export default withTranslation()(AdminSidebar)